import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from "@angular/core";
import { DocumentPayloadModel } from "../../models/document-payload.model";
import { DocumentService } from "../../services/document.service";
import { TypeQrLink } from "../../../../shared/enums/enums/typeQrLink.enum";
import { DocumentFacadeService } from "../../services/store/document-facade.service";
import { DocumentModel } from "../../models/document.model";
import { SharedModule } from "../../../../shared/shared.module";
import { NgTemplateOutlet } from "@angular/common";
import { firstValueFrom } from "rxjs";
import imageCompression from "browser-image-compression";
import { LogoComponent } from "../../../../shared/components/logo/logo.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { QrLinkWrapperComponent } from "../../../../shared/components/qr-link-wrapper/qr-link-wrapper.component";
import { VideoService } from "./video.service";
import { MatMenuModule } from "@angular/material/menu";

@Component({
  selector: "vl-file-uploader",
  standalone: true,
  imports: [
    SharedModule,
    NgTemplateOutlet,
    LogoComponent,
    ChipsComponent,
    QrLinkWrapperComponent,
    MatMenuModule,
  ],
  templateUrl: "./file-uploader.component.html",
  styleUrls: ["./file-uploader.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploaderComponent {
  documentService = inject(DocumentService);

  default = input(false);
  forArticle = input(false);
  simple = input(false);
  readOnly = input(false);
  documentType = input<"image" | "all" | "video">("all");
  mode = input<"mobile" | "desktop">("desktop");

  uploadedDoc = output<DocumentModel>();

  initing = signal(false);
  uploading = model(false);
  uploadPercent = signal(0);
  processing = signal(false);
  processPercent = signal(0);
  initProgressPercent = signal(0);
  typeCompression = signal<"MediaRecorder" | "FFMpeg">(null);
  acceptType = computed(() => {
    switch (this.documentType()) {
      case "image":
        return "image/*";
      case "video":
        return "video/*";
      case "all":
      default:
        return "*/*";
    }
  });
  videoService = inject(VideoService);

  private documentFacade = inject(DocumentFacadeService);

  async onFileSelected(event) {
    try {
      let file: File = event.target.files[0];
      if (file && file.type.startsWith("image/")) {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        try {
          const compressedBlob = await imageCompression(file, options);
          file = new File([compressedBlob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
        } catch (error) {
          console.error("Erreur de compression:", error);
          throw error;
        }
      }
      if (file && file.type.startsWith("video/")) {
        const video = document.createElement("video");
        // const supportMediaRecorder =
        //   typeof MediaRecorder !== "undefined" &&
        //   MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") &&
        //   ("captureStream" in video || "mozCaptureStream" in video);
        // if (false) {
        //   this.typeCompression.set("MediaRecorder");
        //   file = await this.videoService.compressVideoWithMediaRecorder(
        //     file,
        //     this.processPercent,
        //   );
        // } else {
        //   this.typeCompression.set("FFMpeg");
        //   file = await this.videoService.compressVideoWithFFmpeg(
        //     file,
        //     this.processPercent,
        //   );
        // }
        this.typeCompression.set("FFMpeg");
        this.initing.set(true);
        if (!this.videoService.isLoaded()) {
          await this.videoService.loadFFmpeg(this.initProgressPercent);
        }
        this.initing.set(false);
        this.typeCompression.set("FFMpeg");
        this.processing.set(true);
        this.processPercent.set(0);
        file = await this.videoService.compressVideoWithFFmpeg(
          file,
          this.processPercent,
        );
      }
      this.uploadPercent.set(0);
      this.processing.set(false);
      this.uploading.set(true);
      const fileType = await this.documentFacade.upload(
        file,
        this.uploadPercent,
      );
      await this.uploadDoc({ filename: fileType.id });
    } finally {
      this.uploading.set(false);
      this.processing.set(false);
    }
  }

  async onQrCompleted(body: { docId: string }) {
    if (body.docId) {
      this.uploading.set(true);
      try {
        const doc = await this.documentService.loadById(body.docId);
        this.uploadedDoc.emit(doc);
      } finally {
        this.uploading.set(false);
      }
    }
  }

  async uploadDoc(documentPayloadModel: DocumentPayloadModel) {
    const doc = await firstValueFrom(
      this.documentService.uploadLocalCall(documentPayloadModel),
    );
    this.uploadedDoc.emit(doc as DocumentModel);
  }

  protected readonly TypeQrLink = TypeQrLink;
}
